/**
 * Obligaciones — la agenda de lo que Clean Blue tiene que pagar.
 *
 * Nació de una frase del dueño: «se me complica tener noción de las diferentes
 * fechas que debo tener en consideración para los pagos de mis empleados y
 * demás, o temas tributarios, el pago del celular que se utiliza para atención
 * y cosas de ese tipo». El sistema sabía cuándo hay que ir a limpiar y cuándo
 * le toca pagar a un cliente; no sabía nada de esto.
 *
 * La pantalla contesta una sola pregunta —**¿qué me toca pagar y cuándo?**— y
 * la contesta en este orden: primero lo que ya venció, después lo que viene,
 * agrupado por mes. Los montos son estimados y se dicen como tales: la planilla
 * y el IVA cambian todos los meses, y un número inventado sería peor que un
 * guion.
 *
 * Cero lógica de negocio acá (regla 2): la proyección de vencimientos, qué está
 * vencido y qué se puede marcar pagado lo decide el Worker. Esto renderiza.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dateLabel, formatMoney, parseMoneyInput, periodLabel, today,
  BILLING_FREQUENCY, BILLING_FREQUENCY_LABEL, OBLIGATION_CATEGORY,
  OBLIGATION_CATEGORY_LABEL, OBLIGATION_CATEGORY_TONE,
  OBLIGATION_STATUS_LABEL, OBLIGATION_STATUS_TONE,
  type BillingFrequency, type ObligationCategory, type ObligationStatus,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import {
  Async, Badge, Button, Card, Cell, Empty, Field, Modal, Money, Note, PageHead, Stat, Table,
} from '@/components/ui';

/** Un vencimiento. `occurrence_id` en null = todavía no tiene fila propia. */
interface DueItem {
  obligation_id: string;
  name: string;
  category: ObligationCategory;
  frequency: BillingFrequency;
  /** Céntimos como texto; null = varía o no se sabe. */
  amount: string | null;
  amount_paid: string | null;
  period_start: string;
  due_date: string;
  status: ObligationStatus;
  occurrence_id: string | null;
  paid_on: string | null;
  note: string | null;
  is_overdue: boolean;
  /**
   * La corrida de planilla de ese mes, si ya se corrió (solo en categoría
   * `PAYROLL`). Es lo que une esta pantalla con la de Personal: la agenda LEE
   * la corrida, no guarda una copia del monto.
   */
  payroll_run_id: string | null;
  payroll_cost: string | null;
}

interface Upcoming {
  from: string;
  days: number;
  items: DueItem[];
  /** Los contadores los calcula el Worker. Acá no se suma dinero ni se filtra por fecha. */
  counts: { overdue: number; pending: number; due_this_week: number; variable: number };
  /** Céntimos como texto: la suma de los estimados **conocidos**. */
  estimated: string | null;
}

interface Obligation {
  id: string;
  name: string;
  category: ObligationCategory;
  frequency: BillingFrequency;
  amount: string | null;
  due_day: number;
  starts_on: string;
  ended_at: string | null;
  notes: string | null;
  is_active: boolean;
  next_due: string | null;
}

const UPCOMING = '/admin/obligations/upcoming?days=90';

export function ObligationsPage() {
  const upcoming = useApi<Upcoming>(UPCOMING);
  const catalog = useApi<{ obligations: Obligation[] }>('/admin/obligations');
  const [creating, setCreating] = useState(false);
  const [paying, setPaying] = useState<DueItem | null>(null);

  return (
    <>
      <PageHead
        title="Obligaciones"
        subtitle="Lo que la empresa tiene que pagar: planilla, impuestos, servicios, alquiler y seguros."
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>Nueva obligación</Button>
        }
      />

      <Async
        query={upcoming}
        empty={(d) => d.items.length === 0 && (
          <Empty
            icon="reportes"
            title="Nada por vencer en los próximos 90 días"
            hint="Registrá acá lo que pagás todos los meses —planilla, IVA, el celular de atención— y te avisa antes de la fecha."
            action={<Button variant="primary" onClick={() => setCreating(true)}>Nueva obligación</Button>}
          />
        )}
      >
        {(data) => {
          const overdue = data.items.filter((i) => i.is_overdue);
          const pending = data.items.filter((i) => !i.is_overdue);

          return (
            <>
              <div className="grid grid--3">
                <Stat
                  accent={data.counts.overdue > 0}
                  label="Vencidas"
                  value={data.counts.overdue}
                  meta={data.counts.overdue === 0 ? 'Nada atrasado' : 'Sin pagar y ya pasó la fecha'}
                />
                <Stat
                  label="Esta semana"
                  value={data.counts.due_this_week}
                  meta="Vence en los próximos 7 días"
                />
                <Stat
                  label="Estimado por pagar"
                  value={<Money value={data.estimated} size="lg" />}
                  meta={data.counts.variable > 0
                    ? `${data.counts.variable} sin monto estimado, fuera del total`
                    : 'Todas tienen monto estimado'}
                />
              </div>

              {/* Lo vencido va arriba y aparte, no mezclado en su mes: el mes de
                  algo que ya pasó no es la información que hace falta. */}
              {overdue.length > 0 && (
                <div className="mt-3">
                  <Card title={`Vencidas (${overdue.length})`} flush>
                    <DueTable items={overdue} onPay={setPaying} />
                  </Card>
                </div>
              )}

              {groupByMonth(pending).map(([month, items]) => (
                <div className="mt-3" key={month}>
                  <Card title={capitalize(periodLabel(month))} flush>
                    <DueTable items={items} onPay={setPaying} />
                  </Card>
                </div>
              ))}

              <div className="mt-3">
                <Note tone="info">
                  Los montos son estimados y sirven para presupuestar. Al marcar
                  una obligación como pagada se guarda lo que realmente costó.
                  Esto es una agenda: no asienta el gasto en Finanzas.
                  {' '}<strong>La planilla es la excepción</strong>: su monto sale
                  de la corrida del mes en Personal —que sí asienta— y por eso acá
                  se muestra el número real y no un estimado. Un mes con la planilla
                  ya corrida deja de aparecer como vencido.
                </Note>
              </div>
            </>
          );
        }}
      </Async>

      <div className="mt-3">
        <Async query={catalog}>
          {(data) => (
            <Card title="Obligaciones registradas" flush>
              {data.obligations.length === 0 ? (
                <Empty icon="obligaciones" title="Todavía no hay obligaciones registradas" />
              ) : (
                <Table head={
                  /* Este catálogo es de referencia (crear/editar), no la
                     agenda de vencimientos — eso ya lo cubren las tablas de
                     arriba. Obligación (con su categoría) y Estimado son lo
                     que identifica y cuánto cuesta; frecuencia, día del mes y
                     próximo vencimiento son detalle de configuración. */
                  <tr>
                    <th className="tbl--anchor">Obligación</th>
                    <th className="col-hide-sm">Cada cuánto</th><th className="col-hide-sm">Día</th>
                    <th className="right">Estimado</th><th className="right col-hide-sm">Próximo</th>
                  </tr>
                }>
                  {data.obligations.map((o) => (
                    <tr key={o.id}>
                      <td className="tbl--anchor">
                        <Cell sub={o.notes ?? undefined}>
                          <span className="strong">{o.name}</span>{' '}
                          <Badge tone={OBLIGATION_CATEGORY_TONE[o.category]} plain>
                            {OBLIGATION_CATEGORY_LABEL[o.category]}
                          </Badge>
                        </Cell>
                      </td>
                      <td className="small muted col-hide-sm">{BILLING_FREQUENCY_LABEL[o.frequency]}</td>
                      <td className="small muted col-hide-sm">{o.due_day}</td>
                      <td className="right"><Money value={o.amount} dim={o.amount == null} /></td>
                      <td className="right small muted col-hide-sm">
                        {o.next_due ? dateLabel(o.next_due) : '—'}
                      </td>
                    </tr>
                  ))}
                </Table>
              )}
            </Card>
          )}
        </Async>
      </div>

      {creating && <ObligationModal onClose={() => setCreating(false)} />}
      {paying && <PayModal item={paying} onClose={() => setPaying(null)} />}
    </>
  );
}

/* ── Tabla de vencimientos ────────────────────────────────────────────────── */

/** La pestaña de Planilla, en el mes que le corresponde a este vencimiento. */
function payrollLink(periodStart: string): string {
  return `/admin/personal?tab=planilla&period=${periodStart.slice(0, 7)}`;
}

function DueTable({ items, onPay }: { items: DueItem[]; onPay: (i: DueItem) => void }) {
  return (
    <Table head={
      /* La fecha exacta es secundaria acá: la tabla ya está agrupada por mes
         (o es la de «Vencidas»), así que lo que se mira primero es cuál
         obligación es y cuánto/cómo está — Monto y Estado, con Acción
         siempre visible porque es la única forma de marcarla pagada en el
         teléfono. */
      <tr>
        <th className="col-hide-sm">Vence</th><th className="tbl--anchor">Obligación</th>
        <th className="right">Monto</th>
        <th>Estado</th><th className="right">Acción</th>
      </tr>
    }>
      {items.map((i) => (
        <tr key={`${i.obligation_id}-${i.period_start}`}>
          <td className={`col-hide-sm ${i.is_overdue ? 'strong' : 'small muted'}`}>
            {dateLabel(i.due_date)}
          </td>
          <td className="tbl--anchor">
            <Cell sub={`${BILLING_FREQUENCY_LABEL[i.frequency]} · ${periodLabel(i.period_start)}`}>
              <span className="strong">{i.name}</span>{' '}
              <Badge tone={OBLIGATION_CATEGORY_TONE[i.category]} plain>
                {OBLIGATION_CATEGORY_LABEL[i.category]}
              </Badge>
            </Cell>
          </td>
          <td className="right">
            {/* Con la planilla corrida el monto es REAL, no estimado, y se dice.
                Sale de la corrida —no hay una segunda copia del número acá. */}
            {i.payroll_run_id
              ? <Cell sub="según la corrida"><Money value={i.payroll_cost} /></Cell>
              : i.status === 'PAID'
                ? <Money value={i.amount_paid ?? i.amount} />
                : <Money value={i.amount} dim={i.amount == null} />}
          </td>
          <td>
            {i.is_overdue
              ? <Badge tone="danger">Vencida</Badge>
              : (
                <Badge tone={OBLIGATION_STATUS_TONE[i.status]}>
                  {OBLIGATION_STATUS_LABEL[i.status]}
                </Badge>
              )}
            {i.payroll_run_id && i.status !== 'PAID' && (
              <span className="tbl__sub">Planilla del mes ya corrida</span>
            )}
            {i.paid_on && <span className="tbl__sub">Pagada el {dateLabel(i.paid_on)}</span>}
          </td>
          <td className="right">
            {/* La planilla se corre en Personal, que es donde se asienta. Desde
                acá se va hasta el mes que falta en vez de pedirle al dueño que
                lo busque. */}
            {i.category === 'PAYROLL' && (
              <>
                <Link className="btn" to={payrollLink(i.period_start)}>
                  {i.payroll_run_id ? 'Ver la corrida' : 'Correr la planilla'}
                </Link>{' '}
              </>
            )}
            {i.status === 'PENDING' && (
              <Button onClick={() => onPay(i)}>Marcar pagada</Button>
            )}
          </td>
        </tr>
      ))}
    </Table>
  );
}

/* ── Alta ─────────────────────────────────────────────────────────────────── */

/* Las opciones salen de las listas compartidas —las mismas de las que salen los
   `CHECK` de la migración—: un selector con una opción que la base rechaza es
   un 422 esperando a que alguien lo elija. */
const CATEGORIES = OBLIGATION_CATEGORY;
const FREQUENCIES = BILLING_FREQUENCY;

/**
 * Alta de una obligación.
 *
 * El monto es **opcional** a propósito y lo dice el hint: la planilla y el IVA
 * varían, y obligar a poner un número llevaría a inventarlo. Dejarlo vacío
 * significa «varía», que es un dato real.
 */
function ObligationModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({
    name: '',
    category: 'OTHER' as ObligationCategory,
    frequency: 'MONTHLY' as BillingFrequency,
    due_day: '15',
    amount: '',
    starts_on: today(),
    notes: '',
  });

  const amountMinor = form.amount ? parseMoneyInput(form.amount) : null;
  const day = Number.parseInt(form.due_day, 10);
  const dayOk = Number.isInteger(day) && day >= 1 && day <= 28;

  const mutation = useApiMutation(
    () => api.post('/admin/obligations', {
      name: form.name,
      category: form.category,
      frequency: form.frequency,
      due_day: day,
      amount: amountMinor,
      starts_on: form.starts_on,
      notes: form.notes || null,
    }),
    ['/admin/obligations', '/admin/dashboard'],
    { onSuccess: onClose },
  );

  return (
    <Modal
      title="Nueva obligación"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={!form.name || !dayOk || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Registrar
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      <Field label="Nombre" required hint="Como lo llamás vos: «Planilla», «IVA», «Celular de atención».">
        <input
          className="input"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
      </Field>

      <Field label="Tipo" required>
        <select
          className="select"
          value={form.category}
          onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as ObligationCategory }))}
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{OBLIGATION_CATEGORY_LABEL[c]}</option>
          ))}
        </select>
      </Field>

      <Field label="Cada cuánto vence" required>
        <select
          className="select"
          value={form.frequency}
          onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value as BillingFrequency }))}
        >
          {FREQUENCIES.map((f) => (
            <option key={f} value={f}>{BILLING_FREQUENCY_LABEL[f]}</option>
          ))}
        </select>
      </Field>

      <Field
        label="Día del mes"
        required
        error={dayOk ? undefined : 'Un día entre 1 y 28.'}
        hint="Hasta 28: así la fecha existe también en febrero. Si tu fecha real es el 30, poné 28 y anotalo."
      >
        <input
          className="input"
          type="number"
          min={1}
          max={28}
          value={form.due_day}
          onChange={(e) => setForm((f) => ({ ...f, due_day: e.target.value }))}
        />
      </Field>

      <Field
        label="Monto estimado (₡)"
        hint={amountMinor
          ? `Se guarda ${formatMoney(amountMinor)} como estimado.`
          : 'Opcional. Dejalo vacío si varía —la planilla y el IVA cambian todos los meses—.'}
      >
        <input
          className="input input--money"
          type="text"
          inputMode="numeric"
          placeholder="₡0"
          value={form.amount}
          onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
        />
      </Field>

      <Field label="Desde" required hint="Desde cuándo rige. También ancla el ciclo si no es mensual.">
        <input
          className="input"
          type="date"
          value={form.starts_on}
          onChange={(e) => setForm((f) => ({ ...f, starts_on: e.target.value }))}
        />
      </Field>

      <Field label="Notas">
        <input
          className="input"
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
        />
      </Field>
    </Modal>
  );
}

/* ── Marcar pagada ────────────────────────────────────────────────────────── */

/**
 * El monto real, no el estimado.
 *
 * Se prellena con el estimado cuando existe —casi siempre está cerca— pero se
 * guarda lo que la persona confirme: la diferencia entre lo presupuestado y lo
 * pagado es justamente lo que hace útil el histórico.
 */
function PayModal({ item, onClose }: { item: DueItem; onClose: () => void }) {
  const [form, setForm] = useState({
    paid_on: today(),
    amount: item.amount ? String(Number.parseInt(item.amount, 10) / 100) : '',
    note: '',
  });

  const amountMinor = form.amount ? parseMoneyInput(form.amount) : null;

  const mutation = useApiMutation(
    () => api.post(`/admin/obligations/${item.obligation_id}/pay`, {
      period_start: item.period_start,
      paid_on: form.paid_on,
      amount: amountMinor,
      note: form.note || null,
    }),
    ['/admin/obligations', '/admin/dashboard'],
    { onSuccess: onClose },
  );

  return (
    <Modal
      title={`Marcar pagada · ${item.name}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            Marcar pagada
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      <Note tone={item.is_overdue ? 'warn' : 'info'}>
        {periodLabel(item.period_start)} · vencía el {dateLabel(item.due_date)}
        {item.is_overdue && ' — ya pasó la fecha.'}
      </Note>

      <div className="mt-2">
        <Field label="Fecha de pago" required>
          <input
            className="input"
            type="date"
            value={form.paid_on}
            onChange={(e) => setForm((f) => ({ ...f, paid_on: e.target.value }))}
          />
        </Field>

        <Field
          label="Monto pagado (₡)"
          hint={amountMinor
            ? `Se registra ${formatMoney(amountMinor)}.`
            : 'Opcional. Si lo dejás vacío queda registrado el pago sin monto.'}
        >
          <input
            className="input input--money"
            type="text"
            inputMode="numeric"
            placeholder="₡0"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
          />
        </Field>

        <Field label="Nota">
          <input
            className="input"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          />
        </Field>
      </div>
    </Modal>
  );
}

/* ── Ayudas de presentación ───────────────────────────────────────────────── */

/** Agrupa por mes conservando el orden por fecha que ya trae el servidor. */
function groupByMonth(items: DueItem[]): [string, DueItem[]][] {
  const groups = new Map<string, DueItem[]>();
  for (const i of items) {
    const month = `${i.due_date.slice(0, 7)}-01`;
    const list = groups.get(month);
    if (list) list.push(i); else groups.set(month, [i]);
  }
  return [...groups.entries()];
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
